import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { TruncatedText } from '@/components/commons/truncated-text'
import { ArrowLeftIcon } from '@/components/ui/icons'
import { RSVP_TONE } from '@/features/marquee/components/rsvp-status-badge'
import {
	formatAttendeeLanguage,
	rsvpStatusToLabelKey,
	segmentLabel
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeAttendee } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Two-letter monogram from a name (first + last initial). */
const initials = (name: string): string => {
	const matched = name.match(/\b\w/g)
	if (!matched) {
		return '?'
	}
	return (
		matched.length <= 2
			? matched.join('')
			: `${matched[0]}${matched.at(-1)}`
	).toUpperCase()
}

interface HostRailProps {
	/**
	 * The roster row the user clicked. There is no attendee-detail endpoint and
	 * there is not going to be one (OPSSTD-4180 pins "no new endpoint"), so this
	 * row is the whole profile.
	 */
	attendee: MarqueeAttendee
	/** Back-link target (the tab the user came from). */
	backTo: string
	/** Back-link label, e.g. "Back to Attendees". */
	backLabel: string
}

export function AttendeeDetailHostRail({
	attendee,
	backTo,
	backLabel
}: HostRailProps) {
	const { t } = useTranslation()
	const rsvp = attendee.rsvp_status
	// An empty name is possible on a hand-added row; `-` keeps the heading and
	// the monogram from rendering blank.
	const name = attendee.name || '-'
	const segment = segmentLabel(attendee.segment)
	const language = formatAttendeeLanguage(attendee.language)

	return (
		<div className='flex l:w-[420px] l:shrink-0 flex-col gap-6 bg-grey-100 l:px-10 px-4 pt-6 pb-10'>
			<Link
				className='flex items-center gap-3 text-neutral-500 text-sm hover:text-neutral-900'
				to={backTo}
			>
				<span className='flex size-9 items-center justify-center rounded-full border border-neutral-300'>
					<ArrowLeftIcon className='size-4' />
				</span>
				{backLabel}
			</Link>

			<div className='flex size-16 items-center justify-center rounded-full bg-blue-300 font-medium text-2xl text-blue-800'>
				{initials(name)}
			</div>

			<div className='flex flex-col gap-1'>
				<h1 className='font-medium text-neutral-900 text-xl'>{name}</h1>
				{/* HIDDEN: `role` has no source at all — no column, no FR, no
				    contract field. It only ever existed in the mock, synthesised
				    from the attendee id, and rendered on this line beside the
				    real company where the two were indistinguishable. Company
				    alone is real, so the line stays with company only.

				    Naming note for whoever restores this: a different `role`
				    (creator / facilitator) is landing on the operator model, so
				    a host job title needs another name — `jobTitle`.

				    Original: {[role, attendee.company].filter(Boolean).join(' · ')} */}
				{attendee.company && (
					<p className='text-neutral-500 text-sm'>
						{attendee.company}
					</p>
				)}
			</div>

			<div className='flex flex-wrap gap-2'>
				{segment !== '-' && (
					<Badge className='border border-neutral-300 bg-grey-200 text-grey-1000'>
						{segment}
					</Badge>
				)}
				{/* No badge at all when the import carried no RSVP for this row:
				    "pending" would be a state the API never reported. */}
				{rsvp && (
					<Badge className={RSVP_TONE[rsvp]}>
						{t(rsvpStatusToLabelKey[rsvp])}
					</Badge>
				)}
			</div>

			<DetailCard title={t('marquee:attendee_detail_salesforce')}>
				<SpecRow label={t('marquee:attendee_detail_child_account')}>
					{attendee.sf_child_account_id ? (
						<span className='break-all font-medium text-neutral-900 underline'>
							{attendee.sf_child_account_id}
						</span>
					) : (
						<MissingValue />
					)}
				</SpecRow>
				<SpecRow label={t('marquee:attendee_detail_parent_account')}>
					{attendee.sf_parent_account_id ? (
						<span className='break-all font-medium text-neutral-900'>
							{attendee.sf_parent_account_id}
						</span>
					) : (
						<MissingValue />
					)}
				</SpecRow>
				<SpecRow label={t('marquee:attendee_detail_host_id')}>
					{attendee.airbnb_host_id ? (
						<span className='font-medium text-neutral-900'>
							{attendee.airbnb_host_id}
						</span>
					) : (
						<MissingValue />
					)}
				</SpecRow>
				<SpecRow isLast label={t('marquee:attendee_detail_language')}>
					{language ? (
						<span className='font-medium text-neutral-900'>
							{language}
						</span>
					) : (
						<MissingValue />
					)}
				</SpecRow>
			</DetailCard>

			{/* HIDDEN: Active listings. No column, no import source, no resolver
			    read. Closing it needs the live Salesforce pull that sits in the
			    backlog, not in this cycle. The card listed synthesised names and
			    a count in its heading, so it asserted a specific number of
			    listings per host.

			    Restore the markup below against real data. There is no view-model
			    any more — the component reads the roster row directly — so this
			    needs a `listings` array of `{ id, name, status }` on
			    `MarqueeAttendee`, read as `attendee.listings`.

			<DetailCard
				title={t('marquee:attendee_detail_active_listings', {
					count: attendee.listings.length
				})}
			>
				{attendee.listings.map((listing, index) => (
					<SpecRow
						isLast={index === attendee.listings.length - 1}
						key={listing.id}
						label={
							<span className='font-medium text-neutral-900 text-sm'>
								{listing.name}
							</span>
						}
					>
						<span
							className={cn(
								'font-semibold text-sm',
								listing.status === 'active'
									? 'text-success-700'
									: 'text-neutral-500'
							)}
						>
							{t(
								listing.status === 'active'
									? 'marquee:attendee_detail_listing_active'
									: 'marquee:attendee_detail_listing_inactive'
							)}
						</span>
					</SpecRow>
				))}
			</DetailCard>
			*/}
		</div>
	)
}

function Badge({
	children,
	className
}: {
	children: ReactNode
	className?: string
}) {
	return (
		<span
			className={cn(
				'inline-flex items-center rounded-full px-3 py-1.5 font-medium text-xs',
				className
			)}
		>
			{children}
		</span>
	)
}

function DetailCard({
	title,
	children
}: {
	title: string
	children: ReactNode
}) {
	return (
		<div className='flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-neutral-white p-6 shadow-airbnb-3'>
			<p className='font-semibold text-neutral-500 text-sm uppercase'>
				{title}
			</p>
			<div className='flex flex-col gap-4'>{children}</div>
		</div>
	)
}

function SpecRow({
	label,
	children,
	isLast
}: {
	label: ReactNode
	children: ReactNode
	isLast?: boolean
}) {
	return (
		<div
			className={cn(
				'flex items-start justify-between gap-4 text-sm',
				!isLast && 'border-neutral-200 border-b pb-4'
			)}
		>
			{typeof label === 'string' ? (
				<TruncatedText className='shrink-0 text-neutral-500 text-xs'>
					{label}
				</TruncatedText>
			) : (
				<div className='min-w-0'>{label}</div>
			)}
			<div className='min-w-0 text-right'>{children}</div>
		</div>
	)
}

function MissingValue() {
	const { t } = useTranslation()
	return (
		<span className='text-neutral-500'>
			{t('marquee:attendee_missing')}
		</span>
	)
}
